// src/pages/TicketManagement.js
import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../services/api';

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [status, setStatus] = useState('Open');
  const chatEndRef = useRef(null);

  useEffect(() => { fetchTickets(); }, []);
  
  // Scroll to bottom of chat automatically
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selectedTicket?.replies]);

  // 🔥 NAYA: LIVE CHAT SYNC (SILENT AUTO-REFRESH) 🔥
  useEffect(() => {
    let interval;
    // Jab chat modal open hoga, tab ye background me messages check karega
    if (selectedTicket) {
      interval = setInterval(async () => {
        try {
          // Bina loading spinner ghumaye chup-chap data laao
          const response = await apiClient.get('/admin/tickets/all');
          if (response.data.success) {
            setTickets(response.data.tickets); // Table data update karo
            
            // Current open ticket find karo
            const updatedTicket = response.data.tickets.find(t => t._id === selectedTicket._id);
            
            // Agar vendor ka koi naya message aaya hai, toh chat window update kar do
            if (updatedTicket && updatedTicket.replies.length !== selectedTicket.replies.length) {
               setSelectedTicket(updatedTicket);
            }
          }
        } catch (e) {
          // Silent error, no alert
        }
      }, 2000); // Har 2 seconds me check karega
    }
    return () => clearInterval(interval);
  }, [selectedTicket]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/tickets/all');
      if (response.data.success) setTickets(response.data.tickets);
    } catch (error) { console.error(error); } 
    finally { setLoading(false); }
  };

  const handleUpdateTicket = async (e) => {
    e.preventDefault();
    if (!replyText.trim() && status === selectedTicket.status) return;

    try {
      const response = await apiClient.put(`/admin/tickets/update/${selectedTicket._id}`, {
        status: status, adminReply: replyText
      });

      if (response.data.success) {
        // Fast UI Update (Optimistic)
        if (replyText.trim() !== '') {
           const newReply = { sender: 'admin', message: replyText, date: new Date().toISOString() };
           setSelectedTicket({...selectedTicket, status: status, replies: [...selectedTicket.replies, newReply]});
        } else {
           setSelectedTicket({...selectedTicket, status: status});
        }
        
        setReplyText('');
        // Background list update
        fetchTicketsSilently(); 
      }
    } catch (error) {
      alert("Failed to send reply.");
    }
  };

  // Helper for updating table without loading spinner
  const fetchTicketsSilently = async () => {
    try {
      const response = await apiClient.get('/admin/tickets/all');
      if (response.data.success) setTickets(response.data.tickets);
    } catch (error) { }
  };

  return (
    <div className="admin-page-container">
      <div className="page-header">
        <h2>Support Tickets</h2>
        <p>Chat directly with Vendors, Riders, and Users.</p>
      </div>

      {loading ? <p>Loading tickets...</p> : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Type</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Messages</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(ticket => (
                <tr key={ticket._id}>
                  <td>{`#TKT-${ticket._id.substring(0, 4).toUpperCase()}`}</td>
                  <td style={{ textTransform: 'capitalize' }}>{ticket.userType}</td>
                  <td>{ticket.subject}</td>
                  <td>
                    <span className={`status-badge ${ticket.status === 'Open' ? 'bg-warning' : ticket.status === 'Resolved' ? 'bg-success' : 'bg-primary'}`} style={{ padding: '4px 8px', borderRadius: '4px', color: '#fff', fontSize: '12px' }}>
                      {ticket.status}
                    </span>
                  </td>
                  <td>{ticket.replies?.length || 0} msgs</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => { setSelectedTicket(ticket); setStatus(ticket.status); }}
                    >
                      Open Chat
                    </button>
                  </td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center' }}>No Support Tickets Found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔥 CHAT MODAL FOR ADMIN 🔥 */}
      {selectedTicket && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: '#fff', borderRadius: '12px', width: '500px', maxWidth: '90%', display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
            
            {/* Header */}
            <div style={{ padding: '15px 20px', backgroundColor: '#355ca9', color: '#fff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px' }}>{`#TKT-${selectedTicket._id.substring(0, 4).toUpperCase()}`}</h3>
                <small>{selectedTicket.subject}</small>
              </div>
              <button onClick={() => setSelectedTicket(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' }}>✖</button>
            </div>

            {/* Chat History Box */}
            <div style={{ flex: 1, padding: '20px', overflowY: 'auto', backgroundColor: '#F8FAFC' }}>
              {selectedTicket.replies?.map((reply, idx) => {
                const isAdmin = reply.sender === 'admin';
                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start', marginBottom: '15px' }}>
                    <div style={{ maxWidth: '80%', padding: '10px 15px', borderRadius: '15px', backgroundColor: isAdmin ? '#355ca9' : '#E2E8F0', color: isAdmin ? '#fff' : '#000', borderBottomRightRadius: isAdmin ? 0 : '15px', borderBottomLeftRadius: isAdmin ? '15px' : 0 }}>
                      <p style={{ margin: 0, fontSize: '14px' }}>{reply.message}</p>
                      <small style={{ fontSize: '10px', opacity: 0.7, display: 'block', textAlign: 'right', marginTop: '5px' }}>{reply.sender.toUpperCase()}</small>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Input Box */}
            <form onSubmit={handleUpdateTicket} style={{ padding: '15px', backgroundColor: '#fff', borderTop: '1px solid #E2E8F0', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                </select>
                <input 
                  type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type a reply..."
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
                  disabled={selectedTicket.status === 'Resolved'}
                />
                <button type="submit" disabled={selectedTicket.status === 'Resolved'} style={{ padding: '10px 20px', backgroundColor: '#ea6724', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                  Send
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
};

export default TicketManagement;